import {useQuery} from '@tanstack/react-query'
import hash from 'object-hash'

import {API_ENDPOINT_ALIASES, API_ENDPOINT_DEPLOYMENTS} from '../constants'
import {type Sanity, type Vercel} from '../types'
import fetcher from '../utils/fetcher'

type Options = {
  enabled?: boolean
}

const useDeployments = (deploymentTarget: Sanity.DeploymentTargetConfig, options?: Options) => {
  const fetchUrl = fetcher(deploymentTarget)

  // Fetch deployments
  const deployParams = new URLSearchParams()
  deployParams.set('limit', String(deploymentTarget?.deployLimit))

  const {
    data: deploymentsData,
    isFetching: deploymentsIsFetching,
    isSuccess: deploymentsIsSuccess,
    error: deploymentsError,
    refetch,
  } = useQuery<{deployments: Vercel.Deployment[]}>({
    queryKey: [hash(deploymentTarget)],
    queryFn: () => fetchUrl(API_ENDPOINT_DEPLOYMENTS, deployParams),
    enabled: options?.enabled ?? true,
    refetchInterval: 20000, // ms
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  })

  // Fetch aliases (only if deployments have been retrieved)
  const aliasParams = new URLSearchParams()
  aliasParams.set('limit', '20')

  const {
    data: aliasesData,
    isFetching: aliasesIsFetching,
    isSuccess: aliasesIsSuccess,
    error: aliasesError,
  } = useQuery<{
    aliases: Vercel.Alias[]
    pagination: {
      count: number
      next?: number
      prev?: number
    }
  }>({
    queryKey: [hash(deploymentTarget), 'aliases'],
    queryFn: () => fetchUrl(API_ENDPOINT_ALIASES, aliasParams),
    enabled: !!deploymentsData,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const aliases = aliasesData?.aliases

  let deploymentsWithAlias: Vercel.DeploymentWithAlias[] | undefined

  if (aliases) {
    deploymentsWithAlias = deploymentsData?.deployments?.map((val: Vercel.DeploymentWithAlias) => {
      const alias = aliases.find((a) => a.deploymentId === val.uid)
      return Object.assign(val, {alias: alias?.alias})
    })
  }

  return {
    deployments: deploymentsWithAlias,
    error: aliasesError || deploymentsError,
    isFetching: aliasesIsFetching || deploymentsIsFetching,
    isSuccess: aliasesIsSuccess && deploymentsIsSuccess,
    refetch,
  }
}

export default useDeployments
